<template>
  <b-row>
    <b-col>
      <div class="payment-tabs-container">
        <PaymentMethodTab
          v-for="(option, index) in options"
          :key="index"
          :payment-method="option"
          :active="isActive(option)"
          @click="handlePaymentMethodChange(option)"
        >
          <template #content-payment>
            <div v-show="isActive(option)">
              <component
                :is="getPaymentComponent(option)"
                v-bind="optionProps(option)"
                @show-paypal="active = 'paypal'"
                @payment="payment"
              />
            </div>
          </template>
        </PaymentMethodTab>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import PaymentMethodTab from '@/components/PurchaseFlow/PaymentMethodTab';
import PayPal from '@/components/PurchaseFlow/Methods/PayPal';
import CreditCard from '@/components/PurchaseFlow/Methods/CreditCard';
import Stripe from '@/components/PurchaseFlow/Methods/Stripe';
import StripeElements from '@/components/PurchaseFlow/Methods/StripeElements';

const PAYMENT_METHODS = {
  PAYPAL: 'paypal',
  STRIPE: 'stripe',
  BLUESNAP: 'bluesnap',
  STRIPE_ELEMENTS: 'stripe-elements'
};

const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHODS.PAYPAL]: 'PayPal',
  [PAYMENT_METHODS.STRIPE]: 'Stripe',
  [PAYMENT_METHODS.BLUESNAP]: 'BlueSnap',
  [PAYMENT_METHODS.STRIPE_ELEMENTS]: 'Stripe elements'
};

export default {
  name: 'Payments',

  components: {
    PaymentMethodTab,
    PayPal,
    CreditCard,
    Stripe,
    StripeElements
  },

  data() {
    return {
      active: '',
      paymentMethods: Object.values(PAYMENT_METHODS),
      options: [],
      default: ''
    };
  },

  computed: {
    ...mapState('plans', ['openPlan']),
    ...mapGetters('payments', ['getPurchaseMethods']),

    paymentName() {
      return PAYMENT_METHOD_LABELS[this.active] || 'Other';
    }
  },

  watch: {
    active() {
      this.$emit('methodChange', this.active);
    },

    async openPlan(newPlan) {
      if (!newPlan) return;

      await this.initializePaymentOptions(newPlan);
      this.fireGTMPaymentInfo(newPlan);
    }
  },

  async mounted() {
    this.$dataLayerPush('purchase_flow_payment_start');
    await this.initializePaymentOptions(this.openPlan);
    this.fireGTMPaymentInfo(this.openPlan);
  },

  methods: {
    handlePaymentMethodChange(option) {
      this.$dataLayerPush('purchase_flow_payment_method_change', {
        paymentMethod: option
      });
      this.active = option;
    },

    getPaymentComponent(option) {
      const componentMap = {
        [PAYMENT_METHODS.PAYPAL]: 'PayPal',
        [PAYMENT_METHODS.STRIPE]: 'Stripe',
        [PAYMENT_METHODS.BLUESNAP]: 'CreditCard',
        [PAYMENT_METHODS.STRIPE_ELEMENTS]: 'StripeElements'
      };
      return componentMap[option];
    },

    async initializePaymentOptions(plan) {
      const { options: queryOptions, default: defaultMethod } = this.$route.query;
      const filteredOptions = queryOptions
        ?.split(',')
        .filter((option) => this.paymentMethods.includes(option));

      if (filteredOptions?.length) {
        this.options = filteredOptions;
        this.default = this.options.includes(defaultMethod) ? defaultMethod : this.options[0];
      } else {
        await this.$store.dispatch('payments/GET_PURCHASE_METHODS', { plan: plan.id });
        this.options = this.getPurchaseMethods.options;
        this.default = this.options.includes(this.getPurchaseMethods.default)
          ? this.getPurchaseMethods.default
          : this.options[0];
      }

      if (plan?.hasFreeTrial) {
        this.options = this.options.filter(
          (option) =>
            option === PAYMENT_METHODS.STRIPE_ELEMENTS || option === PAYMENT_METHODS.STRIPE
        );
        this.default = this.options[0];
      }

      this.active = this.default;
    },

    payment() {
      this.$emit('payment');
    },

    isActive(option) {
      return this.active === option;
    },

    isDefault(option) {
      return this.default === option;
    },

    optionProps(option) {
      return {
        active: this.isActive(option),
        default: this.isDefault(option)
      };
    },

    fireGTMPaymentInfo(data) {
      if (!data) return;
      this.$dataLayerPush('add_payment_info', {
        addPaymentInfo: data
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.title {
  font-size: 22px;
  font-weight: 500;
  color: #777777;
}
.payment-tabs-container {
  position: relative;
  flex-direction: column;
}
</style>
